import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  message,
  Modal,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CopyOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import PvTable from "../../components/Table/PvTable";
import Popup from "../../components/Popup";
import PvForm from "../../components/Form/PvForm";
import useExternalAppService from "../../services/SuperAdmin/externalAppService";

const { Text } = Typography;

const ExternalAppsPage = () => {
  const {
    getAllExternalApps,
    listLoading,
    listResponse,
    createExternalApp,
    createLoading,
    updateExternalApp,
    updateLoading,
    rotateToken,
    rotateLoading,
    deleteExternalApp,
    deleteLoading,
  } = useExternalAppService();

  const [tableData, setTableData] = useState([]);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isTokenOpen, setIsTokenOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [newToken, setNewToken] = useState("");

  useEffect(() => {
    getAllExternalApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listResponse) return;

    const mapped = listResponse.map((item, index) => ({
      key: index + 1,
      id: item.id,
      name: item.name,
      description: item.description || "—",
      is_active: item.is_active,
      total_calls: item.total_calls,
      created_by: item.created_by
        ? `${item.created_by.first_name} ${item.created_by.last_name}`
        : "—",
      created_at: item.created_at,
    }));
    setTableData(mapped);
  }, [listResponse]);

  const handleCopyToken = () => {
    navigator.clipboard.writeText(newToken);
    message.success("Token copied to clipboard.");
  };

  const handleRegister = async (values) => {
    try {
      const res = await createExternalApp(values);
      setNewToken(res?.api_token || "");
      setIsRegisterOpen(false);
      setIsTokenOpen(true);
      getAllExternalApps();
    } catch {
      message.error("Failed to register external app.");
    }
  };

  const handleEdit = async (values) => {
    try {
      await updateExternalApp(selectedApp.id, values);
      message.success("App updated successfully.");
      setIsEditOpen(false);
      setSelectedApp(null);
      getAllExternalApps();
    } catch {
      message.error("Failed to update app.");
    }
  };

  const handleRotate = (app) => {
    Modal.confirm({
      title: "Rotate API Token",
      content: `This will immediately invalidate the current token for "${app.name}". The external app will lose access until the new token is configured. Continue?`,
      okText: "Rotate Token",
      okButtonProps: { style: { background: "#00573F" } },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await rotateToken(app.id);
          setNewToken(res?.api_token || "");
          setIsTokenOpen(true);
          getAllExternalApps();
        } catch {
          message.error("Failed to rotate token.");
        }
      },
    });
  };

  const handleDeactivate = (app) => {
    Modal.confirm({
      title: "Deactivate External App",
      content: `This will revoke all tokens and block access for "${app.name}". Continue?`,
      okText: "Deactivate",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteExternalApp(app.id);
          message.success(`"${app.name}" has been deactivated.`);
          getAllExternalApps();
        } catch {
          message.error("Failed to deactivate app.");
        }
      },
    });
  };

  const handleToggleActive = async (app, checked) => {
    try {
      await updateExternalApp(app.id, { is_active: checked });
      message.success(`"${app.name}" ${checked ? "activated" : "deactivated"}.`);
      getAllExternalApps();
    } catch {
      message.error("Failed to update status.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    { title: "App Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
          style={{ background: isActive ? "#00573F" : undefined }}
        />
      ),
    },
    {
      title: "Total API Calls",
      dataIndex: "total_calls",
      key: "total_calls",
      render: (val) => <Tag>{val.toLocaleString()}</Tag>,
    },
    { title: "Registered By", dataIndex: "created_by", key: "created_by" },
    { title: "Registered At", dataIndex: "created_at", key: "created_at" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              onClick={() => {
                setSelectedApp(record);
                setIsEditOpen(true);
              }}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Rotate API Token">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={rotateLoading}
              onClick={() => handleRotate(record)}
            >
              Rotate Token
            </Button>
          </Tooltip>
          <Tooltip title="Revoke access and deactivate">
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              loading={deleteLoading}
              onClick={() => handleDeactivate(record)}
            >
              Deactivate
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const registerFormFields = useMemo(
    () => [
      {
        name: "name",
        label: "App Name",
        rules: [{ required: true, message: "Please enter an app name." }],
      },
      {
        name: "description",
        label: "Description",
      },
    ],
    []
  );

  const editFormFields = useMemo(
    () => [
      {
        name: "name",
        label: "App Name",
        rules: [{ required: true, message: "Please enter an app name." }],
      },
      {
        name: "description",
        label: "Description",
      },
    ],
    []
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button
          size="large"
          type="primary"
          style={{ background: "#00573F" }}
          icon={<PlusOutlined />}
          onClick={() => setIsRegisterOpen(true)}
        >
          Register External App
        </Button>
      </Space>

      <PvTable
        data={tableData}
        columns={columns}
        loading={listLoading || updateLoading}
        onRowClick={() => {}}
      />

      {/* Register Modal */}
      <Popup
        title="Register External Application"
        isOpen={isRegisterOpen}
        footer={null}
        onCancel={() => setIsRegisterOpen(false)}
      >
        <PvForm
          isService={true}
          formFields={registerFormFields}
          onSubmitted={handleRegister}
        />
      </Popup>

      {/* Edit Modal */}
      <Popup
        title="Edit External Application"
        isOpen={isEditOpen}
        footer={null}
        onCancel={() => {
          setIsEditOpen(false);
          setSelectedApp(null);
        }}
      >
        <PvForm
          isService={true}
          formFields={editFormFields}
          initialValues={selectedApp}
          onSubmitted={handleEdit}
        />
      </Popup>

      {/* Token Display Modal — shown once after register or rotate */}
      <Modal
        title={
          <Space>
            <KeyOutlined />
            <span>API Token — Save This Now</span>
          </Space>
        }
        open={isTokenOpen}
        onCancel={() => {
          setIsTokenOpen(false);
          setNewToken("");
        }}
        footer={[
          <Button
            key="copy"
            type="primary"
            style={{ background: "#00573F" }}
            icon={<CopyOutlined />}
            onClick={handleCopyToken}
          >
            Copy Token
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsTokenOpen(false);
              setNewToken("");
            }}
          >
            I've saved it, Close
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="danger">
            This token will <strong>not</strong> be shown again. Copy and store
            it securely before closing this window.
          </Text>
          <Input.Password
            value={newToken}
            readOnly
            visibilityToggle
            style={{ fontFamily: "monospace" }}
          />
        </Space>
      </Modal>
    </>
  );
};

export default ExternalAppsPage;
